import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import {
  AccountReportFilterActions,
  AccountReportPageHeader,
  AccountReportTableShell,
} from "@/components/accounts/AccountReportShell";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { formatReportDate, money } from "@/lib/accounts/ledgerReportUtils";
import {
  downloadTrialBalanceExcel,
  downloadTrialBalancePdf,
  fetchTrialBalance,
  type TrialBalance,
} from "@/lib/accounts/trialBalanceStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { Scale } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

const TrialBalancePage = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [report, setReport] = useState<TrialBalance | null>(null);
  const [asAt, setAsAt] = useState("");
  const [hideZero, setHideZero] = useState(true);

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";

  const activeFilters = useMemo(
    () => ({
      asAt: asAt || null,
      hideZero,
    }),
    [asAt, hideZero],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  const applyFilters = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchTrialBalance(activeFilters);
      setReport(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[TrialBalance] Load failed", error);
      toast.error("Could not load trial balance.");
    } finally {
      setLoading(false);
    }
  }, [canView, activeFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadTrialBalancePdf(activeFilters);
          toast.success("PDF downloaded.");
        } else {
          await downloadTrialBalanceExcel(activeFilters);
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[TrialBalance] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, exporting, activeFilters],
  );

  const resetFilters = () => {
    setAsAt("");
    setHideZero(true);
    setReport(null);
    setHasApplied(false);
  };

  const groupedLines = useMemo(() => {
    if (!report) return [];
    const byClass = new Map<string, TrialBalance["lines"]>();
    for (const line of report.lines) {
      const key = line.account_class || "Other";
      const list = byClass.get(key) ?? [];
      list.push(line);
      byClass.set(key, list);
    }
    return Array.from(byClass.entries());
  }, [report]);

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <AccountReportPageHeader
          icon={<Scale className="h-5 w-5 text-primary" />}
          title="Trial Balance"
          description="Closing balance for every chart-of-accounts leaf as at a date. Total debits should equal total credits."
        />

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">As at date</Label>
              <DatePartsPicker
                value={asAt}
                onChange={setAsAt}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                placeholder="All dates (optional)"
              />
            </div>

            <div className="flex items-end gap-2 pb-1 sm:col-span-2">
              <Switch id="hide-zero-balances" checked={hideZero} onCheckedChange={setHideZero} />
              <Label htmlFor="hide-zero-balances" className="cursor-pointer text-xs font-medium text-foreground">
                Hide zero-balance accounts
              </Label>
            </div>
          </div>

          <AccountReportFilterActions
            hasApplied={hasApplied}
            loading={loading}
            exporting={exporting}
            onApply={() => void applyFilters()}
            onReset={resetFilters}
            onExport={(format) => void exportReport(format)}
            statusText={
              hasApplied ? (
                <>
                  Trial balance
                  {asAt ? (
                    <>
                      {" · As at "}
                      <span className="font-semibold text-foreground">{formatReportDate(asAt)}</span>
                    </>
                  ) : (
                    " · All posted dates"
                  )}
                  {report ? (
                    <>
                      {" · "}
                      <span
                        className={cn(
                          "font-semibold",
                          report.totals.balanced ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400",
                        )}
                      >
                        {report.totals.balanced ? "Balanced" : "Out of balance"}
                      </span>
                    </>
                  ) : null}
                </>
              ) : (
                "Choose as-at date (optional), then click Apply filter."
              )
            }
          />
        </div>

        <AccountReportTableShell
          loading={loading}
          hasApplied={hasApplied}
          notAppliedMessage="No trial balance loaded yet. Apply a filter to view accounts."
        >
          {!report || report.lines.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No account balances for this filter.</p>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead className="text-right">Debit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                    <TableHead className="text-right">Credit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedLines.map(([accountClass, lines]) => (
                    <Fragment key={accountClass}>
                      <TableRow className="bg-muted/20 hover:bg-muted/20">
                        <TableCell colSpan={5} className="py-2 text-xs font-bold uppercase tracking-wide text-primary/80">
                          {accountClass}
                        </TableCell>
                      </TableRow>
                      {lines.map((line) => (
                        <TableRow key={line.account_id}>
                          <TableCell className="font-mono text-xs text-muted-foreground">{line.account_code || "—"}</TableCell>
                          <TableCell className="min-w-[10rem] font-medium">{line.account_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{line.account_class}</TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                            {money(line.debit)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                            {money(line.credit)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3} className="font-semibold text-foreground">
                      Total
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(report.totals.debit)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(report.totals.credit)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </AccountReportTableShell>
      </div>
    </Layout>
  );
};

export default TrialBalancePage;
